import { type ComponentProps, type ReactElement, useRef } from 'react';
import cn from 'clsx';
import { type TFunction } from '@pkgs/components/providers/AppContextProvider';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import BellPlus from '@pkgs/components/assets/icons/small/BellPlus.svg';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';
import { Button } from '@ev-tech/ui-core-components/Button';
import { CrossNakedIcon as CrossNaked } from '@ev-tech/search-modules-shared/Icons';

import { ListItem } from './ListItem';
import { Popover, PopoverRoot } from '../Popover/Popover';
import { Headline } from '../Headline/Headline';

import styles from './SearchAlertInformation.module.css';

const IconListItem = ({ ...props }: ComponentProps<typeof ListItem>) => <ListItem {...props} className={cn(styles.iconListItem)} />;

export const SearchAlertUsageInformation = ({
  children,
  shouldShowSearchAlertInfo,
  onClose,
}: {
  children: ReactElement;
  shouldShowSearchAlertInfo: boolean;
  onClose: () => void;
}) => {
  const { t, TransComponent } = useAppContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  return (
    <PopoverRoot
      open={shouldShowSearchAlertInfo}
      modal="trap-focus"
      onOpenChange={(open, eventDetails) => {
        if (!open && eventDetails.reason === 'outside-press') {
          eventDetails.cancel();
          return;
        }
        if (!open) onClose();
      }}
    >
      <div className={styles.buttonContainer} ref={containerRef}>
        <div className={styles.buttonContent} ref={triggerRef} data-open={shouldShowSearchAlertInfo}>
          {children}
        </div>
      </div>
      {shouldShowSearchAlertInfo && (
        <Popover
          aria-label={t('search.searchAlert.information.headline')}
          align="center"
          anchor={triggerRef}
          backdropClassName={styles.backdrop}
          className={styles.popover}
          portalContainer={containerRef}
        >
          <div data-testid="search-alert-information-popover">
            <div className={styles.informationContainer}>
              <div>
                <Headline
                  data-testid="search-alert-information-popover_headline"
                  size="h5"
                  as="h5"
                  label={t('search.searchAlert.information.headline')}
                />
                <IconButton
                  className={styles.closeButton}
                  icon={<CrossNaked />}
                  variant="transparent"
                  onClick={onClose}
                  label={t('search.common.close')}
                />
              </div>
              <ol className={styles.orderedList} data-testid="search-alert-information-popover_list">
                <TransComponent
                  t={t as TFunction}
                  i18nKey="search.searchAlert.creation.education"
                  components={{
                    instruction1: <ListItem />,
                    instruction2: <ListItem />,
                    instruction3: <IconListItem />,
                    bellIcon: <BellPlus />,
                  }}
                />
              </ol>
              <Button
                className={styles.dismissButton}
                testId="search-alert-information-popover_close-button"
                size="medium"
                variant="tertiary"
                label={t('search.searchAlert.information.dismiss')}
                onClick={onClose}
              />
            </div>
          </div>
        </Popover>
      )}
    </PopoverRoot>
  );
};
